import type { DeliveryFilterOperator } from '@/features/delivery/types/contracts';
import type { FieldLogSessionListItem } from '@/features/field-log/types/fieldLog';
import { utf8ByteLength } from '@/shared/lib/utf8';
import { z } from 'zod';

export const ASSET_METADATA_FIELD_KEYS = [
  'environment_id',
  'operator_id',
  'task_id',
  'device_id',
  'environment_l3',
  'episode_task_description',
  'episode_camera_shutter',
  'firmware_version',
] as const;

export type AssetMetadataFieldKey = (typeof ASSET_METADATA_FIELD_KEYS)[number];
type AssetMetadata = Record<string, string>;

export interface AssetRejection {
  rejectedAt: string;
  rejectedBy: string;
  reason: string;
}

export const rejectAssetInputSchema = z.object({
  reason: z
    .string()
    .refine((value) => value.trim().length > 0)
    .refine((value) => utf8ByteLength(value) <= 4096),
});

export type RejectAssetInput = z.infer<typeof rejectAssetInputSchema>;

export interface VideoMeta {
  durationMs: number;
  fps: number | null;
  width: number | null;
  height: number | null;
  bitrateKbps: number | null;
}

type McapLayoutProfile = 'time-clustered-v1' | 'source-set-v2';
export type McapVideoIndexVersion = 'mcap-video-index/v2';

export interface McapObjectIdentity {
  object: string;
  generation: string;
  blake3: string;
  sizeBytes: string;
}

interface McapSourceSetV1Member extends McapObjectIdentity {
  memberRank: 0;
  role: 'main';
}

type McapSourceSetV2Member =
  | (McapObjectIdentity & { memberRank: 0; role: 'main' })
  | (McapObjectIdentity & { memberRank: number; role: 'video'; videoChannelId: number });

export interface McapSourceSetUnion {
  messageOrder: 'log_time/member_rank/in_member_ordinal/v1';
  schemas: Array<{
    schemaId: number;
    name: string;
    encoding: string;
    dataBlake3: string;
  }>;
  channels: Array<{
    channelId: number;
    schemaId: number;
    topic: string;
    messageEncoding: string;
    metadata: Record<string, string>;
  }>;
  schemaMappings: Array<{
    memberRank: number;
    sourceSchemaId: number;
    schemaId: number;
  }>;
  channelMappings: Array<{
    memberRank: number;
    sourceChannelId: number;
    channelId: number;
  }>;
}

export type McapSourceSetIdentity =
  | { version: 1; members: [McapSourceSetV1Member] }
  | {
      version: 2;
      manifest: McapObjectIdentity;
      members: McapSourceSetV2Member[];
      union: McapSourceSetUnion;
    };

export interface McapCanonicalRepresentation {
  canonicalObject: string;
  canonicalBlake3: string;
  canonicalFileSizeBytes: string;
  canonicalGcsGeneration?: string;
  normalizerVersion: string;
  layoutProfile: McapLayoutProfile;
}

interface McapMeta extends McapCanonicalRepresentation {
  profile: string;
  library: string;
  startTimeNs: string;
  endTimeNs: string;
  messageCount: string;
  schemaCount: string;
  channelCount: string;
  summaryPresent: boolean;
  fileSizeBytes: string;
  sourceIndexed: boolean;
  sourceSet?: McapSourceSetIdentity;
  scannerVersion: string;
  scanManifestBlake3: string;
  videoIndexBlake3: string;
  videoIndexVersion: McapVideoIndexVersion;
  idrCount: string;
  videoTrackCount: string;
  probe?: unknown;
}

interface AssetBase {
  assetId: string;
  key: string;
  projectId?: string;
  organizationId?: string;
  durationMs?: number;
  sizeBytes?: number;
  metadata: AssetMetadata;
  filename: string;
  contentBlake3: string;
  provenance: 'uploaded' | 'source_import';
  consentState: 'none' | 'release_on_file' | 'revoked';
  rejection: AssetRejection | null;
}

interface VideoProjectAsset extends AssetBase {
  mediaType: 'video';
  videoMeta: VideoMeta;
}

export interface McapProjectAsset extends AssetBase {
  mediaType: 'mcap';
  mcapMeta: McapMeta;
}

export type ProjectAsset = VideoProjectAsset | McapProjectAsset;

/**
 * An asset as returned by the project inventory endpoint. Field Log linkage is
 * intentionally scoped to that endpoint; detail and global catalog responses
 * do not promise these fields. `session` is returned only with `include=fieldLog`.
 */
export type ProjectInventoryAsset = ProjectAsset & {
  fieldLogLinked: boolean;
  session?: FieldLogSessionListItem | null;
};

export interface ProjectAssetsPage {
  items: ProjectInventoryAsset[];
  nextCursor?: string | null;
  totalCount?: number;
}

export type GlobalAsset = ProjectAsset & { projectId: string };

interface AssetSummary {
  count: number;
  totalDurationMs: number;
}

export type AssetSortOrder = 'asc' | 'desc';
export type AssetSortField = 'key' | 'filename';
export type AssetSort = { field: AssetSortField; direction: AssetSortOrder } | null;

export interface AssetCatalogFilters {
  q?: string | null;
  organizationId?: string;
  projectId?: string;
  minDurationMs?: number;
  maxDurationMs?: number;
  minSizeBytes?: number;
  maxSizeBytes?: number;
  metadata?: Record<string, string>;
  metadataPresent?: Record<string, boolean>;
  rejected?: boolean;
  delivery?: string;
  deliveryOperator?: DeliveryFilterOperator;
  limit?: number;
  cursor?: string | null;
  sort?: AssetSort;
}

export interface GlobalAssetsPage {
  items: GlobalAsset[];
  nextCursor?: string | null;
  summary: AssetSummary;
}

export type AssetPreviewStatus = 'queued' | 'running' | 'failed' | 'cancelled' | 'ready';

export interface AssetPreviewStart {
  taskId: string;
  status: AssetPreviewStatus;
}

/** Low-frame-rate, 480p derived preview generated from the complete Asset. */
export interface AssetPreview {
  taskId: string;
  status: AssetPreviewStatus;
  frameCount?: number;
  videoUrl?: string;
  posterUrl?: string;
  videoUrlExpiresAt?: number;
  posterUrlExpiresAt?: number;
}
